import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { policyDocumentDownloadUrl, policyDocumentsClient } from "../apiClient/policyDocumentsClient";
import { useAuth } from "../../gym/auth/useAuth";
import { MasryWidget } from "../components/MasryWidget";
import { POLICY_DIVISIONS, type PolicyDocument } from "../types/policyDocument.types";

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function DocumentList({ documents }: { documents: PolicyDocument[] }) {
  return (
    <ul className="policy-doc-list">
      {documents.map((doc) => (
        <li key={doc.id} className="policy-doc-row">
          <span className="policy-doc-info">
            <span className="policy-doc-name">{doc.fileName}</span>
            <span className="policy-doc-meta">
              {formatFileSize(doc.fileSizeBytes)} · Uploaded {formatDate(doc.uploadedAt)}
            </span>
          </span>
          <a className="btn btn-ghost" href={policyDocumentDownloadUrl(doc.id)} target="_blank" rel="noopener">
            Download
          </a>
        </li>
      ))}
    </ul>
  );
}

export function PoliciesPage() {
  const { isAdmin } = useAuth();
  const [documents, setDocuments] = useState<PolicyDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void policyDocumentsClient
      .getDocuments()
      .then(setDocuments)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load policy documents."))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Policies and Procedures</h1>
          <p className="page-sub">Company policies and guidelines. Ask Masry anytime using the chat bubble in the corner.</p>
        </div>
        {isAdmin && (
          <Link to="/policies/admin" className="btn btn-ghost">
            Manage documents
          </Link>
        )}
      </header>

      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : documents.length === 0 ? (
        <div className="state-panel">
          <p>No policy documents have been uploaded yet.</p>
        </div>
      ) : (
        <>
          {POLICY_DIVISIONS.map((division) => {
            const docsInDivision = documents.filter((d) => d.division === division);
            if (docsInDivision.length === 0) return null;
            return (
              <section key={division} className="policy-division">
                <h2 className="policy-division-heading">{division}</h2>
                <DocumentList documents={docsInDivision} />
              </section>
            );
          })}
          {(() => {
            const uncategorized = documents.filter((d) => !d.division);
            if (uncategorized.length === 0) return null;
            return (
              <section className="policy-division">
                <h2 className="policy-division-heading">Other</h2>
                <DocumentList documents={uncategorized} />
              </section>
            );
          })()}
        </>
      )}

      <MasryWidget />
    </div>
  );
}
